import { useMemo, useRef, useState } from 'react';

import { t } from 'i18next';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Panel, PanelToggleEvent } from 'primereact/panel';
import { SelectButton } from 'primereact/selectbutton';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '@/app/hooks';
import { DonorSchedulingActions } from '@/app/slices/donorScheduling';
import {
  OptionSetsDropdown,
  ShipmentGroupAutoComplete,
  StringMapDropdown,
} from '@/components';
import { StringMapObjectTypeCode } from '@/components/common/string-map-dropdown';
import {
  FREEZE_OPTION_VALUES,
  LOAD_DONOR_PARTNERS_OPTION_VALUES,
  SHIP_OPTION_VALUES,
  TRANSFER_OPTION_VALUES,
} from '@/enums';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { isOpenCombobox, useFormNav } from '@/hooks/useFormNav';
import { DonorSchedule } from '@/types.gql';

import { PartnershipForm } from '../../partnership-form';
import {
  EG_WITH_SUC,
  PartnershipTypeLabel,
  PartnershipTypeValue,
} from '../../partnership-form/constants';
import { TRUE_FALSE_SELECT_OPTIONS } from '../constants';
import { CreateShipmentGroupDialog } from '../CreateShipmentGroupDialog';
import { ViewShipmentGroupDialog } from '../ViewShipmentGroupDialog';

type DonorScheduleFormEmbryosProps = {
  className?: string;
  collapsed: boolean;
  viewOnly?: boolean;
  onCollapseChange: (collapsed: boolean) => void;
};

export const DonorScheduleFormEmbryos = ({
  className = '',
  collapsed,
  viewOnly = false,
  onCollapseChange,
}: DonorScheduleFormEmbryosProps) => {
  const ref = useRef(null);
  const isMasterEnterEnabled = useFeatureFlag('DS_Master_Enter');
  const {
    ref: formNavRef,
    onKeyDownCapture,
    handleEnterKeyAdvance,
  } = useFormNav<HTMLDivElement>({
    selectors: [
      'input:not([type="hidden"]):not([disabled]):not([readonly])',
      'select:not([disabled])',
      '.p-dropdown:not(.p-disabled) input',
      '.p-dropdown:not(.p-disabled) input[role="combobox"]',
      '.p-dropdown:not(.p-disabled) .p-dropdown-label',
      '.p-autocomplete:not(.p-disabled) input',
      '.p-calendar:not(.p-disabled) input',
      '.p-inputmask:not(.p-disabled) input',
      '.p-inputnumber:not(.p-disabled) input',
      '.p-selectbutton:not(.p-disabled) .p-button:first-child',
      '.p-nucleus:not(.p-disabled) .p-button:first-child',
      'textarea:not([disabled]):not([readonly])',
      'button:not([disabled])[tabindex]',
    ],
    wrap: true,
    sortByTabIndex: true,
    skipAmount: 2,
    allowDropdownEnterSelect: isMasterEnterEnabled,
  });

  const isFreezeFieldsNotEditable = useFeatureFlag(
    'Freeze_Fields_Not_Editable',
  );
  const isRequiredFieldAsterisksEnabled = useFeatureFlag(
    'DS_Required_Field_Asterisks',
  );
  const [showCreateShipmentGroup, setShowCreateShipmentGroup] = useState(false);
  const [showViewShipmentGroup, setShowViewShipmentGroup] = useState(false);

  const dispatch = useDispatch();
  const { workingDonorSchedule } = useAppSelector(
    (state) => state.donorScheduling,
  );

  const isDateGreaterThanOrEqualToToday = useMemo(() => {
    return (
      new Date(workingDonorSchedule?.date) >=
      new Date(new Date().setHours(0, 0, 0, 0))
    );
  }, [workingDonorSchedule?.date]);

  const handleAddShipmentGroup = () => {
    setShowCreateShipmentGroup(true);
  };
  const handleViewCurrentShipmentGroup = () => {
    setShowViewShipmentGroup(true);
  };

  const getCollapsedValue = () => {
    return workingDonorSchedule?.loadDonorPartners !==
      LOAD_DONOR_PARTNERS_OPTION_VALUES.YES
      ? true
      : isDateGreaterThanOrEqualToToday
        ? true
        : collapsed;
  };

  return (
    <div
      className={`donor-schedule-form-embryos ${className}`.trim()}
      onKeyDownCapture={(e) => {
        if (e.key === 'Enter') {
          handleEnterKeyAdvance(e);
        } else {
          onKeyDownCapture(e);
        }
      }}
      ref={formNavRef}
    >
      <Panel
        className="column-span"
        data-pr-disabled={!workingDonorSchedule?.donor}
        ref={ref}
        header={`${t('donorScheduling.partnership')} - ${workingDonorSchedule?.donor ? workingDonorSchedule?.donor?.name : t('donor.selectDonor')}`}
        toggleable
        collapsed={getCollapsedValue()}
        onToggle={(e: PanelToggleEvent) => onCollapseChange(e.value)}
      >
        <PartnershipForm.Root>
          <PartnershipForm.Tabs
            types={[
              {
                attributeValue: PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING,
                value:
                  PartnershipTypeLabel[
                    PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING
                  ],
              },
              {
                attributeValue: PartnershipTypeValue.SPECIAL_BILLING,
                value:
                  PartnershipTypeLabel[PartnershipTypeValue.SPECIAL_BILLING],
              },
            ]}
            viewOnly={viewOnly}
          />
        </PartnershipForm.Root>
      </Panel>

      <div className="grid w-full grid-cols-2 gap-6">
        <StringMapDropdown
          label={t('embryo.ship')}
          value={workingDonorSchedule?.ship || undefined}
          objectTypeCode={StringMapObjectTypeCode.SHIP}
          attributeName="tog_ship"
          disabled={
            viewOnly ||
            workingDonorSchedule?.freeze === FREEZE_OPTION_VALUES.FREEZE_ONLY
          }
          onChange={(e: DropdownChangeEvent) => {
            dispatch({
              type: DonorSchedulingActions.UPDATE_SHIP,
              payload: e.value,
            });

            if (e.value === SHIP_OPTION_VALUES.SHIP_ALL) {
              dispatch({
                type: DonorSchedulingActions.UPDATE_FREEZE,
                payload: null,
              });
              dispatch({
                type: DonorSchedulingActions.UPDATE_FREEZE_INSTRUCTIONS,
                payload: null,
              });
              dispatch({
                type: DonorSchedulingActions.UPDATE_FREEZE_METHOD,
                payload: null,
              });
            }
          }}
          tabIndex={1}
        />
        <StringMapDropdown
          appendTo={undefined}
          label={t('embryo.freezeQ')}
          value={workingDonorSchedule?.freeze || undefined}
          objectTypeCode={StringMapObjectTypeCode.FREEZE}
          attributeName="tog_freeze"
          disabled={
            viewOnly ||
            (isFreezeFieldsNotEditable &&
              (workingDonorSchedule?.ship === SHIP_OPTION_VALUES.SHIP_ALL ||
                workingDonorSchedule?.transfer ===
                  TRANSFER_OPTION_VALUES.TRANSFER_ALL))
          }
          onChange={(e: DropdownChangeEvent) => {
            dispatch({
              type: DonorSchedulingActions.UPDATE_FREEZE,
              payload: e.value,
            });
            if (workingDonorSchedule.freezeMethodId === undefined) {
              dispatch({
                type: DonorSchedulingActions.UPDATE_FREEZE_METHOD,
                payload: EG_WITH_SUC,
              });
            }
            if (e.value === FREEZE_OPTION_VALUES.FREEZE_ONLY) {
              dispatch({
                type: DonorSchedulingActions.UPDATE_SHIP,
                payload: null,
              });
              dispatch({
                type: DonorSchedulingActions.UPDATE_SHIPMENT_GROUP,
                payload: null,
              });
              dispatch({
                type: DonorSchedulingActions.UPDATE_TRANSFER,
                payload: null,
              });
              dispatch({
                type: DonorSchedulingActions.UPDATE_EMBRYO_COMMENT,
                payload: null,
              });
              dispatch({
                type: DonorSchedulingActions.UPDATE_IMPLANT_TYPE,
                payload: null,
              });
            }
          }}
          tabIndex={2}
        />

        <div className="flex w-full flex-row gap-2">
          <div className="flex-1">
            <ShipmentGroupAutoComplete
              label={t('embryo.shipmentGroup')}
              defaultValue={
                workingDonorSchedule?.shipmentGroup?.id
                  ? workingDonorSchedule?.shipmentGroup
                  : undefined
              }
              field="name"
              disabled={
                viewOnly ||
                workingDonorSchedule?.freeze ===
                  FREEZE_OPTION_VALUES.FREEZE_ONLY
              }
              onKeyDown={(e) => {
                if (
                  e.key === 'Enter' &&
                  !isMasterEnterEnabled &&
                  !isOpenCombobox(e.currentTarget)
                ) {
                  e.preventDefault();
                  setShowViewShipmentGroup(true);
                }
              }}
              onSelect={(e: AutoCompleteSelectEvent) =>
                dispatch({
                  type: DonorSchedulingActions.UPDATE_SHIPMENT_GROUP,
                  payload: {
                    shipmentGroup: e.value,
                    shipmentGroupId: e.value?.id,
                  } as DonorSchedule,
                })
              }
              tabIndex={3}
            />
          </div>

          <Button
            icon="pi pi-eye"
            className="mt-4"
            tooltip="View Current Shipment Group"
            onClick={handleViewCurrentShipmentGroup}
          />
          <Button
            icon="pi pi-plus"
            className="mt-4"
            tooltip="Add New Shipment Group"
            onClick={handleAddShipmentGroup}
          />
        </div>
        <div className="label-input-group">
          <label>{t('embryo.freezeInstructions')}</label>
          <InputText
            value={workingDonorSchedule?.freezeInstructions || ''}
            disabled={
              viewOnly ||
              (isFreezeFieldsNotEditable &&
                (workingDonorSchedule?.ship === SHIP_OPTION_VALUES.SHIP_ALL ||
                  workingDonorSchedule?.transfer ===
                    TRANSFER_OPTION_VALUES.TRANSFER_ALL))
            }
            onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_FREEZE_INSTRUCTIONS,
                payload: e.target.value,
              })
            }
            tabIndex={4}
          />
        </div>

        <div className="label-input-group">
          <StringMapDropdown
            appendTo={undefined}
            label={t('embryo.transfer')}
            value={workingDonorSchedule?.transfer || undefined}
            objectTypeCode={StringMapObjectTypeCode.TRANSFER}
            attributeName="tog_transfer"
            disabled={
              viewOnly ||
              workingDonorSchedule?.freeze === FREEZE_OPTION_VALUES.FREEZE_ONLY
            }
            onChange={(e: DropdownChangeEvent) => {
              dispatch({
                type: DonorSchedulingActions.UPDATE_TRANSFER,
                payload: e.value ?? null,
              });
              if (e.value === TRANSFER_OPTION_VALUES.TRANSFER_ALL) {
                dispatch({
                  type: DonorSchedulingActions.UPDATE_FREEZE,
                  payload: null,
                });
                dispatch({
                  type: DonorSchedulingActions.UPDATE_FREEZE_INSTRUCTIONS,
                  payload: null,
                });
                dispatch({
                  type: DonorSchedulingActions.UPDATE_FREEZE_METHOD,
                  payload: null,
                });
              }
            }}
            tabIndex={5}
          />
        </div>
        <div className="label-input-group">
          <OptionSetsDropdown
            label={t('embryo.freezeMethod')}
            value={workingDonorSchedule?.freezeMethodId || undefined}
            optionSetsName="Freezing Method"
            disabled={
              viewOnly ||
              (isFreezeFieldsNotEditable &&
                (workingDonorSchedule?.ship === SHIP_OPTION_VALUES.SHIP_ALL ||
                  workingDonorSchedule?.transfer ===
                    TRANSFER_OPTION_VALUES.TRANSFER_ALL))
            }
            onChange={(e: DropdownChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_FREEZE_METHOD,
                payload: e.value,
              })
            }
            tabIndex={6}
          />
        </div>
        <div className="label-input-group">
          <label>{t('embryo.transferInstructions')}</label>
          <InputText
            value={workingDonorSchedule?.embryoComment || ''}
            disabled={
              viewOnly ||
              workingDonorSchedule?.freeze === FREEZE_OPTION_VALUES.FREEZE_ONLY
            }
            onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_EMBRYO_COMMENT,
                payload: e.target.value,
              })
            }
            tabIndex={7}
          />
        </div>

        <div className="label-input-group">
          <label>{t('embryo.tt')}</label>
          <SelectButton
            className="p-nucleus"
            value={workingDonorSchedule?.tt}
            disabled={viewOnly}
            onChange={(e: DropdownChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_TT,
                payload: e.value,
              })
            }
            options={TRUE_FALSE_SELECT_OPTIONS}
            optionLabel="name"
            optionValue="value"
            tabIndex={8}
          />
        </div>
        <div className="label-input-group">
          <StringMapDropdown
            label={
              t('embryo.exportQ') + (isRequiredFieldAsterisksEnabled ? '*' : '')
            }
            value={workingDonorSchedule?.export}
            objectTypeCode={StringMapObjectTypeCode.EXPORT}
            attributeName="tog_export"
            disabled={viewOnly}
            onChange={(e: DropdownChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_EXPORT,
                payload: e.value,
              })
            }
            appendTo={undefined}
            tabIndex={9}
          />
        </div>
        <div className="label-input-group">
          <StringMapDropdown
            appendTo={undefined}
            label={t('embryo.implantType')}
            value={workingDonorSchedule?.implantType || undefined}
            objectTypeCode={StringMapObjectTypeCode.IMPLANT_TYPE}
            attributeName="tog_implanttype"
            disabled={
              viewOnly ||
              workingDonorSchedule?.freeze === FREEZE_OPTION_VALUES.FREEZE_ONLY
            }
            onChange={(e: DropdownChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_IMPLANT_TYPE,
                payload: e.value,
              })
            }
            tabIndex={10}
          />
        </div>
        <div className="label-input-group">
          <StringMapDropdown
            label={t('embryo.nonEUCountry')}
            value={workingDonorSchedule?.nonEUCountry}
            objectTypeCode={StringMapObjectTypeCode.NON_EU_COUNTRY}
            attributeName="tog_noneucountry"
            disabled={viewOnly || workingDonorSchedule?.export !== 1}
            onChange={(e: DropdownChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_NON_EU_COUNTRY,
                payload: e.value,
              })
            }
            appendTo={undefined}
            tabIndex={11}
          />
        </div>
      </div>
      <CreateShipmentGroupDialog
        visible={showCreateShipmentGroup}
        onHide={() => setShowCreateShipmentGroup(false)}
        afterSave={(
          newShipmentGroupName: string,
          newShipmentGroupId: string,
          newShipmentGroupEstimations: number,
        ) => {
          const newShipmentGroup = {
            id: newShipmentGroupId,
            name: newShipmentGroupName,
            estimations: newShipmentGroupEstimations,
          };
          dispatch({
            type: DonorSchedulingActions.UPDATE_SHIPMENT_GROUP,
            payload: {
              shipmentGroup: newShipmentGroup,
              shipmentGroupId: newShipmentGroupId,
            } as DonorSchedule,
          });
        }}
      />
      <ViewShipmentGroupDialog
        visible={showViewShipmentGroup}
        onHide={() => setShowViewShipmentGroup(false)}
        shipmentGroupId={workingDonorSchedule?.shipmentGroupId}
        shipmentGroupName={workingDonorSchedule?.shipmentGroup?.name}
        shipmentGroupEstimations={
          workingDonorSchedule?.shipmentGroup?.estimations
        }
      />
    </div>
  );
};
